import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, Loader2, MapPin } from "lucide-react";

const STATES = [
  { code: "CA", name: "California", pop: "40M" },
  { code: "TX", name: "Texas", pop: "30M" },
  { code: "FL", name: "Florida", pop: "22M" },
];

export default function StateSponsor() {
  const [form, setForm] = useState({
    state: "CA", employer: "", contact_email: "", headline: "", message: "", apply_url: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/state-sponsors/checkout", {
        ...form, origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2 flex items-center gap-2">
        <Star size={14} /> Premium Placement
      </div>
      <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">
        Sponsor a <span className="text-yellow-500">state</span>
      </h1>
      <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
        Get top-of-page placement for every apprentice browsing California, Texas, or Florida jobs. Your custom message appears above all listings for 30 days.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-8">
        {STATES.map((s) => (
          <button
            key={s.code}
            type="button"
            data-testid={`sponsor-state-${s.code}`}
            onClick={() => set("state", s.code)}
            className={`border p-6 text-left transition ${form.state === s.code ? "border-yellow-500 bg-yellow-500/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"}`}
          >
            <div className="font-display text-4xl uppercase leading-none">{s.code}</div>
            <div className="text-white mt-2">{s.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{s.pop} population</div>
          </button>
        ))}
      </div>

      <div className="mt-8 border border-yellow-500/40 bg-yellow-500/5 p-6 flex items-start gap-4">
        <MapPin size={24} className="text-yellow-500 shrink-0" />
        <div>
          <div className="font-display text-2xl uppercase leading-none">30-Day Spot &mdash; <span className="text-yellow-500">$299</span></div>
          <div className="text-sm text-zinc-300 mt-1">Top banner on every {STATES.find((s) => s.code === form.state)?.name} job search + industry filter combined.</div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Employer / Organization" required>
          <input data-testid="sponsor-employer" required value={form.employer} onChange={(e) => set("employer", e.target.value)} placeholder="ACME Trades" className={inputCls} />
        </Field>
        <Field label="Contact Email" required>
          <input data-testid="sponsor-email" type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="hiring@acme.com" className={inputCls} />
        </Field>
        <Field label="Headline" required>
          <input data-testid="sponsor-headline" required value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="ACME is hiring 200 apprentices across California" className={inputCls} />
        </Field>
        <Field label="Message" required>
          <textarea data-testid="sponsor-message" required rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Full training, union wages, health benefits from day one." className={`${inputCls} resize-none`} />
        </Field>
        <Field label="Apply URL" required>
          <input data-testid="sponsor-apply-url" type="url" required value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} placeholder="https://your-company.com/careers" className={inputCls} />
        </Field>

        <button
          data-testid="sponsor-submit"
          disabled={loading}
          className="w-full bg-yellow-500 text-black font-bold uppercase tracking-wider py-4 hover:bg-yellow-400 active:scale-[0.99] transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? <><Loader2 className="animate-spin" size={16} /> Redirecting to checkout…</> : <>Continue to Payment &mdash; $299</>}
        </button>
        <p className="text-xs text-zinc-600 text-center">Test mode. Use card 4242 4242 4242 4242, any future date, any CVC.</p>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-zinc-950 border border-zinc-800 focus:border-yellow-500 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:outline-none transition";
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">{label}{required && <span className="text-yellow-500"> *</span>}</span>
      {children}
    </label>
  );
}
